import type { ArtStyle, City, Resolution, SubscriptionTier } from '@soundbyte/types';

// Vite populates `import.meta.env` at build time in consumer apps. This shared
// package doesn't depend on Vite directly, so we augment `ImportMeta` ourselves
// so the constants below typecheck when the package is compiled standalone or
// when a non-Vite consumer (e.g. tests) imports it. The signature is kept
// compatible with `vite/client`'s declaration so consuming apps don't get
// conflicting-modifier errors.
declare global {
  interface ImportMetaEnv {
    readonly VITE_API_URL?: string;
    readonly VITE_WS_URL?: string;
    readonly VITE_APP_TYPE?: string;
    readonly VITE_MAIN_SITE_URL?: string;
    readonly VITE_PLAYGROUND_URL?: string;
    readonly VITE_ADMIN_URL?: string;
    readonly VITE_AUTH_STORAGE_KEY?: string;
  }
  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

// ============================================
// API Configuration
// ============================================
// Backend API URL - uses environment variable or production URL as default
export const API_BASE_URL = import.meta.env?.VITE_API_URL || 'https://api.amritasethi.com/api';

// WebSocket URL for real-time updates
export const WS_URL = import.meta.env?.VITE_WS_URL || 'https://api.amritasethi.com';

// ============================================
// App Configuration
// ============================================
export const APP_NAME = 'SoundBYTE Playground';

// App type for session isolation - admin or playground
// Each app should set VITE_APP_TYPE in their .env file
export const APP_TYPE = import.meta.env?.VITE_APP_TYPE || 'playground';

// Main site URL - Use environment variable in production
export const MAIN_SITE_URL = import.meta.env?.VITE_MAIN_SITE_URL || 'https://amritasethi.com';

// Development ports
export const WEB_PORT = 8080;
export const PLAYGROUND_PORT = 5174;

// Playground URL - Use environment variable in production
export const PLAYGROUND_URL = import.meta.env?.VITE_PLAYGROUND_URL || 'https://playground.amritasethi.com';

// Admin URL - Use environment variable in production
export const ADMIN_URL = import.meta.env?.VITE_ADMIN_URL || 'https://admin.amritasethi.com';

// ============================================
// Art Styles
// ============================================
export const ART_STYLES: { value: ArtStyle; label: string; description: string }[] = [
  {
    value: 'abstract',
    label: 'Abstract',
    description: 'Free-flowing forms and expressive compositions',
  },
  {
    value: 'geometric',
    label: 'Geometric',
    description: 'Precise shapes and mathematical patterns',
  },
  {
    value: 'organic',
    label: 'Organic',
    description: 'Natural, flowing lines inspired by nature',
  },
  {
    value: 'minimalist',
    label: 'Minimalist',
    description: 'Clean, simple forms with purposeful restraint',
  },
  {
    value: 'vibrant',
    label: 'Vibrant',
    description: 'Bold colors and energetic compositions',
  },
  {
    value: 'monochrome',
    label: 'Monochrome',
    description: 'Elegant single-color variations',
  },
  {
    value: 'cosmic',
    label: 'Cosmic',
    description: 'Space-inspired ethereal aesthetics',
  },
];

// ============================================
// Cities
// ============================================
export const CITIES: { value: City; label: string; icon: string }[] = [
  { value: 'dubai', label: 'Dubai', icon: '🏙️' },
  { value: 'new-york', label: 'New York', icon: '🗽' },
  { value: 'london', label: 'London', icon: '🎡' },
  { value: 'tokyo', label: 'Tokyo', icon: '🗼' },
  { value: 'paris', label: 'Paris', icon: '🗼' },
  { value: 'sydney', label: 'Sydney', icon: '🌉' },
];

// ============================================
// Resolutions
// ============================================
export const RESOLUTIONS: Resolution[] = [
  { width: 1080, height: 1080, label: 'Square (1080x1080)' },
  { width: 1920, height: 1080, label: 'Landscape (1920x1080)' },
  { width: 1080, height: 1920, label: 'Portrait (1080x1920)' },
  { width: 2048, height: 2048, label: 'High-Res Square (2048x2048)' },
  { width: 3840, height: 2160, label: '4K Landscape (3840x2160)' },
];

// ============================================
// Subscription Tiers (BYTEclub Membership)
// Note: Artwork generation is admin-controlled, not credit-based
// Both paid tiers are annual-only. Collectors Circle is by application (no Stripe).
// ============================================
export const SUBSCRIPTION_TIERS: {
  tier: SubscriptionTier;
  name: string;
  subtitle: string;
  price: { monthly: number; yearly: number };
  features: string[];
  entryType: 'open' | 'invitation' | 'application';
}[] = [
  {
    tier: 'free',
    name: 'Free',
    subtitle: 'Explorer',
    price: { monthly: 0, yearly: 0 },
    entryType: 'open',
    features: [
      'Browse the public gallery',
      'Access to the Playground',
      'Stay updated with announcements',
    ],
  },
  {
    tier: 'plus',
    name: 'BYTEclub',
    subtitle: 'Community Circle',
    price: { monthly: 12, yearly: 111 },
    entryType: 'open',
    features: [
      'Join the global community surrounding the studio',
      'Receive Studio Letters and early announcements',
      'Invitations to public moments and exhibitions',
      'Broadcast reflections via WhatsApp',
      'A front-row seat to the evolution of SoundBYTE\u00AE',
    ],
  },
  {
    tier: 'vip',
    name: 'Patron Circle',
    subtitle: 'Annual Patronage',
    price: { monthly: 0, yearly: 1111 },
    entryType: 'invitation',
    features: [
      'Private Patron Letters and early previews of new work',
      'Patron-only access windows before public releases',
      'Occasional invitations to curated studio salons',
      'Recognition as a Founding Patron of the practice',
      'Annual Patron Edition artwork (selected years)',
    ],
  },
  {
    tier: 'collectors' as SubscriptionTier,
    name: 'Collectors Circle',
    subtitle: 'Inner Circle',
    price: { monthly: 0, yearly: 5000 },
    entryType: 'invitation',
    features: [
      'First access to major works and private previews',
      'Collectors Letter with directional insights',
      'Invitations to intimate Table gatherings',
      'Priority consideration for commissions',
      'Alignment with the long-term evolution of the practice',
    ],
  },
];

// ============================================
// Default Values
// ============================================
export const DEFAULT_COLOR_CONFIG = {
  primary: '#10B981',
  secondary: '#3B82F6',
  accent: '#8B5CF6',
  background: '#0A0A0A',
};

export const DEFAULT_RESOLUTION = RESOLUTIONS[0];
export const DEFAULT_ART_STYLE: ArtStyle = 'abstract';
export const DEFAULT_CITY: City = 'dubai';

// ============================================
// Validation
// ============================================
export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 8,
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 100,
  PROMPT_MAX_LENGTH: 500,
  MAX_VOICE_DURATION_SECONDS: 30,
  MAX_FILE_SIZE_MB: 10,
};

// ============================================
// Routes (standalone playground app - no prefix)
// ============================================
export const PLAYGROUND_ROUTES = {
  // Public
  HOME: '/',
  LOGIN: '/login',
  SIGNUP: '/signup',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',

  // Protected - User
  DASHBOARD: '/dashboard',
  CREATE: '/create',
  SONIC: '/sonic',
  ORIGINALS: '/originals',
  SHOP: '/shop',
  COLLECTION_ITEM: '/collection/:slug',
  ORDERS: '/orders',
  ORDERS_SONIC: '/orders/sonic',
  ORDERS_ORIGINALS: '/orders/originals',
  ORDERS_COLLECTION: '/orders/collection',
  GALLERY: '/gallery',
  GENERATION: '/generation/:id',
  SETTINGS: '/settings',
  SUBSCRIPTION: '/subscription',
} as const;

// Admin routes (separate admin app)
export const ADMIN_ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  DASHBOARD: '/',
  USERS: '/users',
  GENERATIONS: '/generations',
  SETTINGS: '/settings',
} as const;

// Alias for backward compatibility
export const ROUTES = PLAYGROUND_ROUTES;
